// Adapted from davinci-elections apps/web/src/theme/color-mode.tsx @ 6e23f58
import { ThemeProvider as NextThemeProvider, useTheme, type ThemeProviderProps } from 'next-themes'
import { useCallback, useEffect, useState } from 'react'

export type ColorMode = 'light' | 'dark'

// The chrome colour of each mode (neutral.100 / neutral.925: the top bar), as in the index.html theme-color metas.
const CHROME_COLOR: Record<ColorMode, string> = { light: '#e2e8e4', dark: '#202523' }

/**
 * Keeps the browser/OS bar colour in step with a hand-picked theme: both theme-color metas take the chosen mode's
 * colour (a media-restricted meta would otherwise follow the device). `system` restores the per-scheme defaults.
 */
export const syncThemeColor = (value: ThemePreference): void => {
  if (typeof document === 'undefined') return
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    const own: ColorMode = (meta.getAttribute('media') ?? '').includes('dark') ? 'dark' : 'light'
    meta.content = CHROME_COLOR[value === 'system' ? own : value]
  }
}

export const ColorModeProvider = ({ children, ...props }: ThemeProviderProps) => (
  <NextThemeProvider
    attribute='class'
    disableTransitionOnChange
    storageKey='theme-preference'
    defaultTheme='system'
    enableSystem
    // The CSP has no inline scripts: next-themes' own inline script is made inert (a data block), and
    // /theme-init.js (index.html) does its job before first paint.
    scriptProps={{ type: 'application/json' }}
    {...props}
  >
    {children}
  </NextThemeProvider>
)

export const useColorMode = (): {
  colorMode: ColorMode
  toggleColorMode: () => void
  setColorMode: (value: ColorMode) => void
} => {
  const { resolvedTheme, setTheme } = useTheme()
  const colorMode: ColorMode = resolvedTheme === 'dark' ? 'dark' : 'light'

  const toggleColorMode = useCallback(() => {
    const next = colorMode === 'dark' ? 'light' : 'dark'
    setTheme(next)
    syncThemeColor(next)
  }, [colorMode, setTheme])

  const setColorMode = useCallback(
    (value: ColorMode) => {
      setTheme(value)
      syncThemeColor(value)
    },
    [setTheme]
  )

  return { colorMode, toggleColorMode, setColorMode }
}

export type ThemePreference = 'system' | 'light' | 'dark'

/** The person's choice, including `system` (follow the device), as opposed to `useColorMode`'s resolved light/dark. */
export const useThemePreference = (): {
  preference: ThemePreference
  setPreference: (value: ThemePreference) => void
} => {
  const { theme, setTheme } = useTheme()
  const preference: ThemePreference = theme === 'light' || theme === 'dark' ? theme : 'system'
  const setPreference = useCallback(
    (value: ThemePreference) => {
      setTheme(value)
      syncThemeColor(value)
      // Choosing System removes the stored entry: nothing is kept unless the person picked a side.
      if (value === 'system') {
        try {
          window.localStorage.removeItem('theme-preference')
        } catch {
          /* storage unavailable */
        }
      }
    },
    [setTheme]
  )
  return { preference, setPreference }
}

export const useColorModeValue = <T,>(light: T, dark: T) => {
  const { colorMode } = useColorMode()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  return mounted && colorMode === 'dark' ? dark : light
}
